import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ArrowRight, ArrowDown, Calendar, Check, Sun, Moon, Play, Home, Layers, Sparkles, Mail, LogIn } from 'lucide-react';
import logo from '../assets/logo.png';
import Dock from '../components/ui/Dock';
import { clamp01, seg, ease, lerp, useLenis } from '../components/home/motionKit';
import { createStudioScene } from '../components/home/homeScene';

// The dock — the original page's signature navigation, carried forward.
// Same options as before (Login kept; the Audits shield retired).
const DOCK_ITEMS = [
  { icon: Home, label: 'Home', href: '#home' },
  { icon: Layers, label: 'The build', href: '#process' },
  { icon: Sparkles, label: 'Services', href: '#services' },
  { icon: Mail, label: 'Contact', href: '#contact' },
];
const DOCK_TRAILING = [{ icon: LogIn, label: 'Login', href: '/login' }];

const MobileNav = () => (
  <nav
    className="fixed bottom-0 inset-x-0 z-50 md:hidden backdrop-blur-xl"
    style={{ background: 'var(--nav-bg)', borderTop: '1px solid var(--hair)' }}
    aria-label="Main navigation"
  >
    <div className="flex items-center justify-around py-2.5 px-2">
      {[...DOCK_ITEMS, ...DOCK_TRAILING].map((item) => {
        const Icon = item.icon;
        const isExternal = !item.href.startsWith('#');
        return (
          <a
            key={item.label}
            href={item.href}
            onClick={(e) => {
              if (!isExternal) {
                e.preventDefault();
                const el = document.getElementById(item.href.replace('#', ''));
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="flex flex-col items-center gap-1 px-3 py-1 t-faint transition-colors"
          >
            <Icon className="w-5 h-5" />
            <span className="text-[10px] font-medium">{item.label}</span>
          </a>
        );
      })}
    </div>
  </nav>
);

/**
 * LAB R2 — "The Studio" · V7 (H2 promotion + theme system).
 * Hero: "The Monument" from the hero lab (engineering-first, serif accent,
 * particle word-halo on "purpose", stamps DESIGNED · ENGINEERED · KEPT,
 * CTAs "Bring us your vision" / "Begin at zero").
 * Theme: LIGHT by default — the particle world renders as ink on paper —
 * with a persisted dark-mode toggle (additive lime starfield).
 * The page-wide particle story is unchanged: motes → plane → monument →
 * ignite → recede + embers → ring.
 */

const BEATS = [
  [0, 0.081],
  [0.081, 0.265],
  [0.265, 0.5],
  [0.5, 0.635],
];

const NARRATIVE = [
  { pre: 'Everything ready-made starts as ', acc: 'noise' },
  { pre: 'So we start at ', acc: 'zero' },
  { pre: 'Structure, engineered from ', acc: 'nothing' },
  { pre: 'Until it becomes ', acc: 'yours' },
];

const RAIL = ['Noise', 'Zero', 'Build', 'Yours', 'The details'];

// The tour: each component of the pixel site gets its close-up + an editorial
// caption in the hero's voice, placed in the space the camera leaves free
const CAPS = [
  {
    n: '01',
    k: 'Navigation',
    pre: 'Made for ',
    acc: 'every',
    post: ' hand.',
    d: 'Fully keyboard-accessible: skip links, focus order, no traps.',
    demo: 'nav',
  },
  {
    n: '02',
    k: 'Type',
    pre: 'Set to be ',
    acc: 'read',
    post: '.',
    d: 'WCAG 2.2 AA contrast, legible for everyone in every light.',
    demo: 'type',
  },
  {
    n: '03',
    k: 'Actions',
    pre: 'Obvious on ',
    acc: 'purpose',
    post: '.',
    d: 'One clear action per moment: instant, and measured.',
    demo: 'cta',
  },
  {
    n: '04',
    k: 'The system',
    pre: 'Built to ',
    acc: 'grow',
    post: '.',
    d: 'A design system underneath: every piece made to scale with you.',
    demo: 'cards',
  },
];
const STOPS_L = [0.65, 0.7353, 0.8206, 0.9059];
const STOP_W = 0.0765;
// where each lens panel sits relative to its component's projected rect
const PANEL_POS = [
  { ax: -0.35, ay: 1, dx: 0, dy: 180 },
  { ax: 0.65, ay: 1, dx: 0, dy: 150 },
  { ax: 1, ay: 0, dx: 260, dy: -20 },
  { ax: 0.35, ay: -1, dx: 0, dy: -175 },
];

// crisp mini-demos — the dust, resolved into the real interface
const Demo = ({ kind }) => {
  if (kind === 'nav') {
    return (
      <div>
        <div className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: 'var(--mock-card)', border: '1px solid var(--mock-line)' }}>
          <div className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 rounded-full bg-[#B58A3A] inline-block" />
            <span className="text-[11px] font-semibold" style={{ color: 'var(--mock-ink)' }}>Harborlight</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] rounded px-1 py-0.5" style={{ color: 'var(--mock-ink)', outline: '2px solid var(--accent)', outlineOffset: '2px' }}>Programs</span>
            <span className="text-[10px]" style={{ color: 'var(--mock-muted)' }}>Spaces</span>
            <span className="text-[10px]" style={{ color: 'var(--mock-muted)' }}>Stories</span>
            <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full" style={{ background: '#B58A3A', color: '#1F1408' }}>Visit</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2.5">
          <span className="kbd">⇥ Tab</span>
          <span className="kbd">↵ Enter</span>
          <span className="text-[10px]" style={{ color: 'var(--mock-muted)' }}>focus you can see</span>
        </div>
      </div>
    );
  }
  if (kind === 'type') {
    return (
      <div>
        <div className="rounded-xl px-4 py-3.5" style={{ background: 'var(--mock-card)', border: '1px solid var(--mock-line)' }}>
          <p className="display text-xl" style={{ color: 'var(--mock-ink)' }}>A place for everyone.</p>
          <p className="text-[11px] mt-1" style={{ color: 'var(--mock-muted)' }}>Programs, spaces, and stories for the whole neighborhood.</p>
        </div>
        <div className="flex items-center gap-2 mt-2.5">
          <span className="kbd">Aa 16–64px</span>
          <span className="kbd">Contrast 13.2 : 1</span>
        </div>
      </div>
    );
  }
  if (kind === 'cta') {
    return (
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[11px] font-semibold px-4 py-2 rounded-full" style={{ background: '#B58A3A', color: '#1F1408' }}>Plan your visit →</span>
          <span className="text-[11px] font-semibold px-4 py-2 rounded-full" style={{ background: '#B58A3A', color: '#1F1408', outline: '2px solid var(--accent)', outlineOffset: '3px' }}>Plan your visit →</span>
        </div>
        <div className="flex items-center gap-2 mt-2.5">
          <span className="kbd">default</span>
          <span className="kbd">focused</span>
          <span className="text-[10px]" style={{ color: 'var(--mock-muted)' }}>one action per view</span>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {['Programs', 'Spaces', 'Stories'].map((c) => (
          <div key={c} className="rounded-lg p-2" style={{ background: 'var(--mock-card)', border: '1px solid var(--mock-line)' }}>
            <div className="w-4 h-4 rounded mb-1.5" style={{ background: 'rgba(181,138,58,0.25)', border: '1px solid rgba(181,138,58,0.4)' }} />
            <p className="text-[9px] font-medium" style={{ color: 'var(--mock-ink)' }}>{c}</p>
            <div className="h-0.5 w-4/5 rounded mt-1" style={{ background: 'var(--mock-dot)' }} />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2.5">
        <span className="kbd">8-px grid</span>
        <span className="text-[10px]" style={{ color: 'var(--mock-muted)' }}>every piece reusable</span>
      </div>
    </div>
  );
};

const SVC_SCENES = [
  {
    n: '01',
    k: 'Platforms',
    pre: 'Built from ',
    acc: 'zero',
    post: '.',
    d: 'Custom-designed, custom-engineered websites and platforms, shaped around your identity and the people you serve. Never adapted from a theme.',
    checks: ['Designed around your identity', 'Hand-engineered, no templates', 'WCAG 2.2 AA as the floor'],
    demo: 'site',
    flip: false,
    rot: '-1.2deg',
  },
  {
    n: '02',
    k: 'Stewardship',
    pre: 'Kept ',
    acc: 'excellent',
    post: '.',
    d: 'A platform is a relationship, not a handoff. We audit monthly, monitor continuously, and stay available for whatever needs to change.',
    checks: ['Audited monthly, reported honestly', 'Monitored continuously', 'A real person, same day'],
    demo: 'care',
    flip: true,
    rot: '1.1deg',
  },
  {
    n: '03',
    k: 'Media & Film',
    pre: 'Filmed when it ',
    acc: 'matters',
    post: '.',
    d: 'When the moment calls for it: events, portraits, and aerial film, produced by our crew and delivered straight into your platform.',
    checks: ['Event coverage', 'Aerial cinematography', 'Delivered into your platform'],
    demo: 'film',
    flip: false,
    rot: '-0.9deg',
  },
];

// stationery demos for the service scenes — the practice, shown real
const SvcDemo = ({ kind }) => {
  if (kind === 'site') {
    return (
      <div>
        <div className="flex items-center gap-1.5 px-4 py-2.5 rounded-t-2xl" style={{ background: 'var(--mock-chrome)', borderBottom: '1px solid var(--mock-line)' }}>
          <span className="w-2 h-2 rounded-full" style={{ background: 'var(--mock-dot)' }} />
          <span className="w-2 h-2 rounded-full" style={{ background: 'var(--mock-dot)' }} />
          <span className="w-2 h-2 rounded-full" style={{ background: 'var(--mock-dot)' }} />
          <span className="text-[9px] ml-2" style={{ color: 'var(--mock-muted)' }}>harborlight.org · built from zero</span>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between mb-3.5">
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-full bg-[#B58A3A] inline-block" />
              <span className="text-[11px] font-semibold" style={{ color: 'var(--mock-ink)' }}>Harborlight</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[9px]" style={{ color: 'var(--mock-muted)' }}>Programs</span>
              <span className="text-[9px]" style={{ color: 'var(--mock-muted)' }}>Spaces</span>
              <span className="text-[9px]" style={{ color: 'var(--mock-muted)' }}>Stories</span>
              <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full" style={{ background: '#B58A3A', color: '#1F1408' }}>Visit</span>
            </div>
          </div>
          <div className="rounded-xl px-4 py-3.5 mb-3" style={{ background: 'var(--mock-card)', border: '1px solid var(--mock-line)' }}>
            <p className="display text-lg" style={{ color: 'var(--mock-ink)' }}>A place for everyone.</p>
            <p className="text-[10px] mt-0.5 mb-2.5" style={{ color: 'var(--mock-muted)' }}>Programs, spaces, and stories for the whole neighborhood.</p>
            <span className="inline-block text-[10px] font-semibold px-3 py-1.5 rounded-full" style={{ background: '#B58A3A', color: '#1F1408' }}>Plan your visit</span>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {['Programs', 'Spaces', 'Stories'].map((c) => (
              <div key={c} className="rounded-lg p-2.5" style={{ border: '1px solid var(--mock-line)' }}>
                <div className="w-5 h-5 rounded mb-1.5" style={{ background: 'rgba(181,138,58,0.22)', border: '1px solid rgba(181,138,58,0.35)' }} />
                <p className="text-[9px] font-medium" style={{ color: 'var(--mock-ink)' }}>{c}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (kind === 'care') {
    return (
      <div className="p-4">
        <div className="grid grid-cols-3 gap-2.5 mb-3">
          <div className="rounded-xl p-3" style={{ background: 'var(--mock-card)', border: '1px solid var(--mock-line)' }}>
            <p className="text-[9px] mb-1" style={{ color: 'var(--mock-muted)' }}>Accessibility</p>
            <p className="display text-xl accent-text">98</p>
            <p className="text-[8px] mt-0.5" style={{ color: 'var(--mock-muted)' }}>July audit</p>
          </div>
          <div className="rounded-xl p-3" style={{ background: 'var(--mock-card)', border: '1px solid var(--mock-line)' }}>
            <p className="text-[9px] mb-1" style={{ color: 'var(--mock-muted)' }}>Uptime · 30d</p>
            <p className="display text-xl" style={{ color: 'var(--mock-ink)' }}>99.98%</p>
            <svg viewBox="0 0 100 18" className="mt-1 w-full" aria-hidden="true">
              <polyline points="0,12 16,10 32,11 48,7 64,9 80,5 100,6" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div className="rounded-xl p-3" style={{ background: 'var(--mock-card)', border: '1px solid var(--mock-line)' }}>
            <p className="text-[9px] mb-1" style={{ color: 'var(--mock-muted)' }}>Support</p>
            <p className="display text-xl" style={{ color: 'var(--mock-ink)' }}>Same-day</p>
            <p className="text-[8px] mt-0.5" style={{ color: 'var(--mock-muted)' }}>a person, not a queue</p>
          </div>
        </div>
        <div className="rounded-xl divide-y" style={{ border: '1px solid var(--mock-line)', borderColor: 'var(--mock-line)' }}>
          {[
            ['July accessibility sweep', 'passed · report delivered'],
            ['Content refresh · summer programs', 'published in 4 hours'],
          ].map(([a, b]) => (
            <div key={a} className="flex items-center justify-between px-3 py-2" style={{ borderColor: 'var(--mock-line)' }}>
              <span className="text-[9px]" style={{ color: 'var(--mock-ink)' }}>{a}</span>
              <span className="text-[8px] flex items-center gap-1.5" style={{ color: 'var(--mock-muted)' }}>
                <Check className="w-2.5 h-2.5" style={{ color: 'var(--accent)' }} />
                {b}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="p-4">
      <div className="rounded-xl overflow-hidden relative mb-2.5" style={{ background: 'var(--mock-card)', border: '1px solid var(--mock-line)' }}>
        <div className="aspect-[16/8] flex items-center justify-center">
          <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--panel-bg)', border: '1px solid var(--mock-line)' }}>
            <Play className="w-3.5 h-3.5 ml-0.5" style={{ color: 'var(--mock-ink)' }} />
          </span>
        </div>
        <div className="absolute bottom-2 left-3 right-3">
          <p className="text-[9px] mb-1" style={{ color: 'var(--mock-muted)' }}>Harborlight, from above the neighborhood</p>
          <div className="h-1 rounded-full" style={{ background: 'var(--mock-line)' }}>
            <div className="h-full w-1/3 rounded-full" style={{ background: 'var(--accent)' }} />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="kbd">Aerial</span>
        <span className="kbd">Event highlights</span>
        <span className="text-[10px]" style={{ color: 'var(--mock-muted)' }}>shot by our crew</span>
      </div>
    </div>
  );
};

const Homepage = () => {
  const [reduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.localStorage.getItem('nexsav-theme') || 'dark';
  });
  useLenis(!reduced);

  const stageWrapRef = useRef(null);
  const heroRef = useRef(null);
  const purposeRef = useRef(null);
  const visionRef = useRef(null);
  const servicesRef = useRef(null);
  const ctaRef = useRef(null);
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const capRefs = useRef([]);
  const svcRowRefs = useRef([]);
  const svcHeadRef = useRef(null);
  const ctaInnerRef = useRef(null);
  const lineRefs = useRef([]);
  const railRefs = useRef([]);
  const lastP = useRef(-1);

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setThemeState(next);
    window.localStorage.setItem('nexsav-theme', next);
  };

  useEffect(() => {
    sceneRef.current?.setTheme(theme);
  }, [theme]);

  useEffect(() => {
    const apply = (p) => {
      // lens panels ride the component close-ups
      for (let i = 0; i < STOPS_L.length; i++) {
        const el = capRefs.current[i];
        if (!el) continue;
        const local = seg(p, STOPS_L[i], STOPS_L[i] + STOP_W);
        const amt =
          local > 0 && local < 1
            ? ease(seg(local, 0, 0.3)) * (1 - ease(seg(local, 0.78, 1)))
            : 0;
        el.style.opacity = String(amt);
        el.style.transform = `translate(-50%, calc(-50% + ${lerp(22, 0, amt)}px))`;
        el.style.pointerEvents = 'none';
      }

      lineRefs.current.forEach((el, i) => {
        if (!el) return;
        const [a, b] = BEATS[i];
        const fadeIn = ease(seg(p, a, a + 0.028));
        const fadeOut = i === BEATS.length - 1 ? ease(seg(p, 0.88, 0.95)) : ease(seg(p, b - 0.025, b));
        el.style.opacity = String(fadeIn * (1 - fadeOut));
        el.style.transform = `translateY(${lerp(16, 0, fadeIn) + lerp(0, -12, fadeOut)}px)`;
      });

      const idx = p < BEATS[0][1] ? 0 : p < BEATS[1][1] ? 1 : p < BEATS[2][1] ? 2 : p < STOPS_L[0] ? 3 : 4;
      railRefs.current.forEach((el, i) => {
        if (el) el.dataset.active = i === idx ? 'true' : 'false';
      });
    };

    if (reduced) {
      apply(1);
      return undefined;
    }

    if (canvasRef.current) {
      try {
        sceneRef.current = createStudioScene(canvasRef.current);
        sceneRef.current.setTheme(
          typeof window !== 'undefined'
            ? window.localStorage.getItem('nexsav-theme') || 'dark'
            : 'dark'
        );
      } catch (e) {
        sceneRef.current = null;
      }
    }

    const onResize = () => sceneRef.current?.resize();
    const onPointer = (ev) => {
      sceneRef.current?.setPointer(
        (ev.clientX / window.innerWidth) * 2 - 1,
        -((ev.clientY / window.innerHeight) * 2 - 1)
      );
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('pointermove', onPointer, { passive: true });

    let raf;
    let lastY = window.scrollY;
    const loop = () => {
      const el = stageWrapRef.current;
      if (el) {
        const vh = window.innerHeight;
        const rect = el.getBoundingClientRect();
        const H = el.offsetHeight;
        const p = clamp01(-rect.top / (H - vh));
        if (Math.abs(p - lastP.current) > 0.0004) {
          apply(p);
          lastP.current = p;
        }
        const heroP = heroRef.current
          ? clamp01(-heroRef.current.getBoundingClientRect().top / (heroRef.current.offsetHeight * 0.85))
          : 0;
        // services drives the recede — complete it as the section ENTERS, not
        // across its (now much taller) full height
        const servicesP = servicesRef.current
          ? clamp01((vh * 0.92 - servicesRef.current.getBoundingClientRect().top) / (vh * 0.92))
          : 0;
        // cta drives the ring — complete it while the finale is on screen
        const ctaP = ctaRef.current
          ? clamp01((vh * 0.95 - ctaRef.current.getBoundingClientRect().top) / (vh * 0.85))
          : 0;
        const y = window.scrollY;
        const vel = clamp01(Math.abs(y - lastY) / 80);
        lastY = y;
        // the halo anchors to "purpose" at the top of the page and to
        // "vision" at the finale — the loop closes on the same gesture
        const visionRect = visionRef.current ? visionRef.current.getBoundingClientRect() : null;
        const anchorEl =
          visionRect && visionRect.top < vh && visionRect.bottom > 0 ? visionRef.current : purposeRef.current;
        if (anchorEl && sceneRef.current) {
          const zr = anchorEl.getBoundingClientRect();
          sceneRef.current.setAnchor(
            ((zr.left + zr.width / 2) / window.innerWidth) * 2 - 1,
            -(((zr.top + zr.height / 2) / window.innerHeight) * 2 - 1)
          );
        }
        sceneRef.current?.setState({ hero: heroP, stage: p, services: servicesP, cta: ctaP, vel });
        sceneRef.current?.render(performance.now() * 0.001);

        // service scenes (and the section header) reveal with the page's physics
        [svcHeadRef.current, ctaInnerRef.current, ...svcRowRefs.current].forEach((el) => {
          if (!el) return;
          const r = el.getBoundingClientRect();
          const amt = ease(clamp01((vh * 0.88 - r.top) / (vh * 0.34)));
          el.style.opacity = String(amt);
          el.style.transform = `translateY(${lerp(36, 0, amt)}px)`;
        });

        // position each active lens panel beside its component's projected rect
        for (let i = 0; i < STOPS_L.length; i++) {
          const el = capRefs.current[i];
          if (!el) continue;
          const local = seg(p, STOPS_L[i], STOPS_L[i] + STOP_W);
          if (local <= 0 || local >= 1) continue;
          const r = sceneRef.current?.getCompRect?.(i);
          if (!r || !r.valid) continue;
          const parent = el.offsetParent;
          const pr = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
          const cfg = PANEL_POS[i];
          let x = r.cx + cfg.ax * (r.w / 2) + cfg.dx;
          let y = r.cy + cfg.ay * (r.h / 2) + cfg.dy;
          x = Math.max(210, Math.min(window.innerWidth - 210, x));
          y = Math.max(150, Math.min(vh - 150, y));
          el.style.left = `${x - pr.left}px`;
          el.style.top = `${y - pr.top}px`;
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onPointer);
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced]);

  return (
    <div
      className={`studio-lab min-h-screen antialiased ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`}
      style={{ overflowX: 'clip', background: 'var(--bg)', color: 'var(--ink)' }}
    >
      <Helmet>
        <title>NexSav · Design · Engineering · New York</title>
        <meta
          name="description"
          content="NexSav is a New York design & engineering studio building custom digital platforms from zero: accessible by default, engineered to last, and cared for long after launch."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Hanken+Grotesk:ital,wght@0,400;0,500;0,600;0,700;1,500&family=Instrument+Serif:ital@1&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <style>{`
        .studio-lab { font-family: 'Hanken Grotesk', 'Inter', system-ui, sans-serif; }
        .studio-lab.theme-light {
          --bg: #F2EFE9; --ink: #1C1B16; --ink-dim: rgba(28,27,22,0.78);
          --muted: #4A473D; --faint: #6E6A5C; --dim: #8B8778;
          --hair: rgba(28,27,22,0.14); --accent: #4D7C0F;
          --accent-glow: none; --accent-glow-sm: none;
          --nar-shadow: 0 1px 16px rgba(242,239,233,0.95), 0 0 5px rgba(242,239,233,0.9);
          --panel-bg: rgba(251,248,242,0.92);
          --nav-bg: rgba(242,239,233,0.78); --card-bg: rgba(28,27,22,0.035); --card-bg-h: rgba(28,27,22,0.06);
          --aurora: rgba(77,124,15,0.1); --sel: rgba(77,124,15,0.22);
          --grain-o: 0.09;
          --mock-bg: #FBF8F2; --mock-chrome: #F3EDE2; --mock-line: rgba(36,27,19,0.12);
          --mock-ink: #241B13; --mock-muted: #6E6257; --mock-card: #F6F0E4; --mock-dot: #D8CFC0;
          --mock-shadow: 0 40px 90px -30px rgba(60,50,30,0.35);
          --mock-topline: inset 0 1px 0 rgba(255,255,255,0.7);
        }
        .studio-lab.theme-dark {
          --bg: #050507; --ink: #ffffff; --ink-dim: rgba(255,255,255,0.72);
          --muted: #AEB4BE; --faint: #8A919C; --dim: #5B6472;
          --hair: rgba(255,255,255,0.08); --accent: #B0FC38;
          --accent-glow: 0 0 50px rgba(176,252,56,0.45); --accent-glow-sm: 0 0 12px rgba(176,252,56,0.8);
          --nar-shadow: 0 2px 28px rgba(5,5,7,0.95), 0 0 10px rgba(5,5,7,0.8);
          --panel-bg: rgba(11,13,17,0.9);
          --nav-bg: rgba(5,5,7,0.7); --card-bg: rgba(255,255,255,0.025); --card-bg-h: rgba(255,255,255,0.045);
          --aurora: rgba(176,252,56,0.15); --sel: rgba(176,252,56,0.28);
          --grain-o: 0.13;
          --mock-bg: #0B0D11; --mock-chrome: rgba(255,255,255,0.02); --mock-line: rgba(255,255,255,0.08);
          --mock-ink: #F4EFE6; --mock-muted: #6B7280; --mock-card: linear-gradient(135deg, #10151d 0%, #0B0D11 100%); --mock-dot: #374151;
          --mock-shadow: 0 50px 120px -30px rgba(0,0,0,0.7);
          --mock-topline: inset 0 1px 0 rgba(255,255,255,0.08);
        }
        .studio-lab ::selection { background: var(--sel); }
        .studio-lab a:focus-visible, .studio-lab button:focus-visible { outline: 2px solid var(--accent); outline-offset: 3px; border-radius: 6px; }
        .studio-lab .label { letter-spacing: 0.18em; text-transform: uppercase; font-weight: 500; }
        .studio-lab .display { font-weight: 600; letter-spacing: -0.028em; line-height: 1.04; }
        .studio-lab .t-ink { color: var(--ink); }
        .studio-lab .t-muted { color: var(--muted); }
        .studio-lab .t-faint { color: var(--faint); }
        .studio-lab .t-dim { color: var(--dim); }
        .studio-lab .grad-text { background: linear-gradient(180deg, var(--ink) 55%, var(--ink-dim)); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .studio-lab .accent-text { color: var(--accent); text-shadow: var(--accent-glow); }
        .studio-lab .hairline { border-color: var(--hair); }
        .studio-lab .serif-acc { font-family: 'Instrument Serif', Georgia, serif; font-style: italic; font-weight: 400; letter-spacing: 0; }
        .studio-lab .ul-link { position: relative; padding-bottom: 3px; }
        .studio-lab .ul-link::after { content: ''; position: absolute; left: 0; bottom: 0; height: 1px; width: 100%;
          background: linear-gradient(90deg, var(--accent), transparent);
          transform: scaleX(0.28); transform-origin: left; transition: transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1); }
        .studio-lab .ul-link:hover::after { transform: scaleX(1); }
        .studio-lab .diamond { width: 4px; height: 4px; transform: rotate(45deg); background: var(--accent); opacity: 0.7; }
        .studio-lab .nar-shadow { text-shadow: var(--nar-shadow); }
        .studio-lab .card { background: var(--card-bg); transition: background 0.3s; }
        .studio-lab .card:hover { background: var(--card-bg-h); }
        .studio-lab .chip-pill { border: 1px solid var(--hair); color: var(--muted); }
        .studio-lab .check-badge { background: color-mix(in srgb, var(--accent) 14%, transparent); border: 1px solid color-mix(in srgb, var(--accent) 42%, transparent); }
        .studio-lab .grain { background-image: url('https://framerusercontent.com/images/6mcf62RlDfRfU61Yg5vb2pefpi4.png'); background-repeat: repeat; background-size: 154px auto; opacity: var(--grain-o); }
        .studio-lab .horizon { height: 1px; background: linear-gradient(90deg, transparent, var(--accent), transparent); opacity: 0.35; }
        .studio-lab .aurora { position: absolute; border-radius: 9999px; filter: blur(90px); pointer-events: none; background: radial-gradient(ellipse, var(--aurora), transparent 65%); }
        .studio-lab [data-active='true'] .rail-dot { background: var(--accent); box-shadow: var(--accent-glow-sm); }
        .studio-lab [data-active='true'] .rail-label { color: var(--ink); opacity: 1; }
        .studio-lab .rail-dot { background: var(--dim); }
        .studio-lab .theme-btn { border: 1px solid var(--hair); color: var(--muted); }
        .studio-lab .theme-btn:hover { color: var(--ink); border-color: var(--accent); }
        .studio-lab .dock-shell { background: var(--nav-bg); border: 1px solid var(--hair); box-shadow: var(--mock-shadow); }
        .studio-lab .dock-tile { background: var(--card-bg); border: 1px solid var(--hair); }
        .studio-lab .dock-tile:hover { background: var(--card-bg-h); }
        .studio-lab .dock-icon { color: var(--muted); }
        .studio-lab .dock-tile:hover .dock-icon { color: var(--accent); }
        .studio-lab .dock-tooltip { background: var(--panel-bg); border: 1px solid var(--hair); color: var(--muted); }
        .studio-lab .dock-divider-line { background: var(--hair); }
        .studio-lab.theme-light .dock-logo { filter: invert(1); }
        .studio-lab .feature-panel { background: var(--panel-bg); backdrop-filter: blur(12px);
          border: 1px solid var(--mock-line); box-shadow: var(--mock-shadow); }
        .studio-lab .kbd { border: 1px solid var(--mock-line); background: var(--mock-chrome);
          color: var(--mock-ink); border-radius: 6px; padding: 2px 7px; font-size: 10px; }
        .studio-lab .svc-tick { width: 0; height: 3px; border-radius: 999px; background: var(--accent); transition: width .45s cubic-bezier(.2,.7,.2,1); }
        .studio-lab .svc-row:hover .svc-tick { width: 2.5rem; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
        .studio-lab .rise { animation: fadeUp 0.85s cubic-bezier(0.2, 0.7, 0.2, 1) backwards; }
        @keyframes cueDrop { 0% { top: -12%; } 100% { top: 112%; } }
        .studio-lab .cue-dot { animation: cueDrop 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite; background: var(--accent); }
        @media (prefers-reduced-motion: reduce) { .studio-lab .rise, .studio-lab .cue-dot { animation: none; } }
      `}</style>

      {!reduced && (
        <canvas ref={canvasRef} className="fixed inset-0 z-0 w-full h-full pointer-events-none" aria-hidden="true" />
      )}
      <div className="grain fixed inset-0 z-[2] pointer-events-none" aria-hidden="true" />

      {/* ---------- floating controls — the dock carries identity & nav ---------- */}
      <div className="fixed top-5 right-5 md:top-6 md:right-8 z-50 flex items-center gap-3">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          className="theme-btn w-11 h-11 rounded-full flex items-center justify-center transition-colors backdrop-blur-xl"
          style={{ background: 'var(--nav-bg)' }}
        >
          {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
        </button>
        <a
          href="https://calendar.app.google/3YiwbqiYDMCS4AQN7"
          target="_blank"
          rel="noopener noreferrer"
          className="px-5 py-3 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] hover:shadow-[0_0_24px_rgba(176,252,56,0.35)] transition-all shadow-lg"
        >
          Book a meeting
        </a>
      </div>

      {/* ---------- hero — The Monument ---------- */}
      <section id="home" ref={heroRef} className="relative min-h-[100svh] flex items-center overflow-hidden">
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 w-full pt-32 pb-24 text-center">
          <p className="rise text-[11px] label t-faint mb-10" style={{ animationDelay: '0.1s' }}>
            NexSav · Design · Engineering · New York
          </p>
          <h1 className="display rise text-[clamp(3rem,8vw,7rem)] leading-[0.98]" style={{ animationDelay: '0.22s' }}>
            <span className="grad-text block">Every pixel,</span>
            <span className="block">
              <span className="grad-text">placed on </span>
              <span ref={purposeRef} className="serif-acc accent-text">purpose</span>
              <span className="grad-text">.</span>
            </span>
          </h1>
          <p className="rise text-base md:text-lg t-muted leading-relaxed max-w-xl mx-auto mt-9" style={{ animationDelay: '0.38s' }}>
            Custom platforms engineered from zero, and the care that keeps them, for the
            institutions people rely on.
          </p>
          <div className="rise mt-11 flex flex-wrap items-center justify-center gap-x-8 gap-y-4" style={{ animationDelay: '0.52s' }}>
            <a
              href="#contact"
              className="group px-8 py-4 rounded-full bg-[#B0FC38] text-gray-900 font-semibold hover:bg-[#9BE22E] hover:shadow-[0_0_32px_rgba(176,252,56,0.4)] transition-all duration-300 flex items-center gap-2 text-sm"
            >
              Bring us your vision
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href="#process"
              className="ul-link text-sm t-muted hover:t-ink transition-colors flex items-center gap-1.5"
            >
              Begin at zero
              <ArrowDown className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="rise mt-20 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-[12px] label t-faint tracking-[0.28em]" style={{ animationDelay: '0.66s' }}>
            {['Designed', 'Engineered', 'Kept'].map((w, i) => (
              <span key={w} className="flex items-center gap-7">
                {i > 0 && <span className="diamond inline-block" aria-hidden="true" />}
                {w}
              </span>
            ))}
          </div>
        </div>

        {/* scroll cue */}
        <div className="absolute bottom-24 md:bottom-8 left-6 md:left-8 flex items-center gap-3" aria-hidden="true">
          <span className="relative w-px h-14 overflow-hidden inline-block" style={{ background: 'var(--hair)' }}>
            <span className="cue-dot absolute left-[-1.5px] w-1 h-1 rounded-full" />
          </span>
          <span className="label text-[10px] t-dim">The build begins at zero</span>
        </div>
      </section>

      {/* ---------- THE BUILD — pinned stage over the world ---------- */}
      <section
        id="process"
        ref={stageWrapRef}
        className="relative"
        style={{ height: reduced ? 'auto' : '1050vh' }}
        aria-label="The build: from noise to a platform of your own, toured detail by detail"
      >
        <div
          className="top-0 h-screen overflow-hidden"
          style={{ position: reduced ? 'relative' : 'sticky', minHeight: reduced ? '100vh' : undefined }}
        >
          <div className="max-w-6xl mx-auto px-6 md:px-8 h-full relative">
            <p className="label absolute top-24 left-6 md:left-8 text-[11px] t-faint">The build</p>

            {/* narrative */}
            <div className="absolute left-6 md:left-8 top-[18%] max-w-[86vw] md:max-w-[40%] z-10">
              {NARRATIVE.map((line, i) => (
                <h2
                  key={line.acc}
                  ref={(el) => (lineRefs.current[i] = el)}
                  className="display t-ink nar-shadow absolute top-0 left-0 text-[clamp(1.7rem,3.1vw,2.8rem)]"
                  style={{ opacity: reduced && i === NARRATIVE.length - 1 ? 1 : 0 }}
                >
                  {line.pre}
                  <span className="serif-acc accent-text text-[1.08em]">{line.acc}</span>
                  .
                </h2>
              ))}
            </div>

            {/* the lens — a crisp stationery panel resolves the dust into real UI */}
            {CAPS.map((c, i) => (
              <div
                key={c.k}
                ref={(el) => (capRefs.current[i] = el)}
                className="feature-panel absolute z-20 w-[360px] max-w-[86vw] rounded-2xl p-5"
                style={{ opacity: 0, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                aria-hidden="true"
              >
                <p className="label text-[10px] mb-1.5" style={{ color: 'var(--mock-muted)' }}>
                  {c.n} · {c.k}
                </p>
                <h3 className="display text-[1.5rem] leading-tight" style={{ color: 'var(--mock-ink)' }}>
                  {c.pre}
                  <span className="serif-acc accent-text text-[1.1em]">{c.acc}</span>
                  {c.post}
                </h3>
                <div className="my-4">
                  <Demo kind={c.demo} />
                </div>
                <p className="text-xs flex items-start gap-2.5" style={{ color: 'var(--mock-muted)' }}>
                  <span className="check-badge w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-2.5 h-2.5" style={{ color: 'var(--accent)' }} />
                  </span>
                  {c.d}
                </p>
              </div>
            ))}

            {/* progress rail */}
            <div className="absolute left-6 md:left-8 bottom-24 md:bottom-12 z-10 flex items-center gap-6" aria-hidden="true">
              {RAIL.map((r, i) => (
                <span key={r} ref={(el) => (railRefs.current[i] = el)} className="flex items-center gap-2.5" data-active="false">
                  <span className="rail-dot w-1.5 h-1.5 rounded-full transition-all duration-300" />
                  <span className="rail-label text-[11px] t-dim opacity-70 transition-colors duration-300">{r}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- services — three scenes in the tour's language ---------- */}
      <section id="services" ref={servicesRef} className="relative">
        <div className="horizon absolute top-0 inset-x-0 opacity-30" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-6 md:px-8 pt-32 md:pt-40 pb-24">
          <div ref={svcHeadRef} style={{ opacity: reduced ? 1 : 0 }}>
            <p className="label text-[11px] t-faint mb-4">What we do</p>
            <p className="display t-ink nar-shadow text-[clamp(1.9rem,3.8vw,3.1rem)] max-w-3xl">
              We believe public-facing platforms should be built like{' '}
              <span className="serif-acc accent-text">public buildings</span>: built to welcome
              everyone, and built to last.
            </p>
          </div>

          {SVC_SCENES.map((c, i) => (
            <div
              key={c.n}
              ref={(el) => (svcRowRefs.current[i] = el)}
              className="grid md:grid-cols-12 gap-10 md:gap-14 items-center py-16 md:py-20"
              style={{ opacity: reduced ? 1 : 0 }}
            >
              <div className={`md:col-span-6 ${c.flip ? 'md:order-2' : ''}`}>
                <p className="label text-[10px] t-faint mb-3">
                  {c.n} · {c.k}
                </p>
                <h3 className="display t-ink nar-shadow text-[clamp(2rem,4vw,3.2rem)]">
                  {c.pre}
                  <span className="serif-acc accent-text text-[1.1em]">{c.acc}</span>
                  {c.post}
                </h3>
                <p className="t-muted leading-relaxed max-w-md mt-5 nar-shadow">{c.d}</p>
                <ul className="mt-6 space-y-2.5">
                  {c.checks.map((chk) => (
                    <li key={chk} className="flex items-center gap-2.5 text-sm t-muted nar-shadow">
                      <span className="check-badge w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0">
                        <Check className="w-2.5 h-2.5" style={{ color: 'var(--accent)' }} />
                      </span>
                      {chk}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`md:col-span-6 ${c.flip ? 'md:order-1' : ''}`}>
                <div className="feature-panel rounded-2xl overflow-hidden max-w-[440px] mx-auto" style={{ transform: `rotate(${c.rot})` }}>
                  <SvcDemo kind={c.demo} />
                </div>
              </div>
            </div>
          ))}

          <p className="text-sm t-faint max-w-2xl nar-shadow">
            Serving education, business, and public service across New York, with the same
            standard of craft for every client.
          </p>
        </div>
      </section>

      {/* ---------- CTA finale — inside the completed ring ---------- */}
      <section id="contact" ref={ctaRef} className="relative overflow-hidden min-h-[100svh] flex items-center">
        <div className="horizon absolute top-0 inset-x-0 opacity-30" aria-hidden="true" />
        <div ref={ctaInnerRef} className="relative max-w-6xl mx-auto px-6 md:px-8 py-24 text-center w-full" style={{ opacity: reduced ? 1 : 0 }}>
          <p className="label text-[11px] t-faint mb-8">Your build</p>
          <h2 className="display t-ink nar-shadow text-[clamp(2.4rem,5.5vw,4.4rem)] max-w-3xl mx-auto">
            Bring us your{' '}
            <span ref={visionRef} className="serif-acc accent-text text-[1.1em]">vision</span>
            .
          </h2>
          <p className="t-muted nar-shadow text-base md:text-lg leading-relaxed max-w-xl mx-auto mt-7">
            You bring the vision. We take it from zero, and we keep it for years.
          </p>
          <div className="mt-11 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="mailto:hello@nexsavinnovate.com"
              className="group px-8 py-4 rounded-full bg-[#B0FC38] text-gray-900 font-semibold hover:bg-[#9BE22E] hover:shadow-[0_0_40px_rgba(176,252,56,0.45)] transition-all duration-300 flex items-center gap-2 text-sm"
            >
              Start the conversation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href="https://calendar.app.google/3YiwbqiYDMCS4AQN7"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 rounded-full border hairline t-ink font-medium hover:border-[var(--accent)] transition-all duration-300 text-sm flex items-center gap-2 backdrop-blur"
            >
              <Calendar className="w-4 h-4" />
              Book a meeting
            </a>
          </div>
          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-[12px] label t-faint tracking-[0.28em]">
            {['Designed', 'Engineered', 'Kept'].map((w, i) => (
              <span key={w} className="flex items-center gap-7">
                {i > 0 && <span className="diamond inline-block" aria-hidden="true" />}
                {w}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- footer ---------- */}
      <footer className="border-t hairline relative pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <img src={logo} alt="NexSav" className="h-6 opacity-70" style={{ filter: theme === 'light' ? 'invert(1)' : 'none' }} />
          <p className="text-xs t-dim">Design · Engineering · New York</p>
          <p className="text-xs t-dim">© 2026 NexSav. All rights reserved.</p>
        </div>
      </footer>

      <Dock items={DOCK_ITEMS} trailingItems={DOCK_TRAILING} />
      <MobileNav />
    </div>
  );
};

export default Homepage;
